/* eslint-disable max-lines */
import { useAuth0 } from '@auth0/auth0-react';
import { openInNewTab } from '@strata/helper-functions';
import { Header as StrataHeader } from '@strata/react-header';
import { BaseNotification } from '@strata/react-notifications';
import { Badge } from '@strata/react-status-icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FeatureCarousel } from 'components/header/FeatureCarousel';
import { ProjectBadge } from 'components/header/ProjectBadge';
import { UserProfileDialog } from 'components/header/UserProfileDialog';
import { NotificationComponentMap } from 'components/notifications/NotificationComponentMap';
import { NotificationsDropdownTabs } from 'components/notifications/NotificationsDropdownTabs';
import { useUnreadCounts } from 'services/APIHooks';
import { clearDataForLogout } from 'services/authHelpers';
import { usePermissionCheck, WRITE_REFERRAL_PERIOD } from 'services/Permissions';
import { AppRoutes } from 'services/routes';
import { useGetHomeScreenMessagesQuery } from 'store/api/announcementApi';
import {
    clearIncomingNotification,
    updateNotificationStatus,
    updateNotificationStatuses,
} from 'store/notification/notificationThunks';

/* Base URLs for external links */
const CONFLUENCE_BASE_URL = 'https://freemarkethealth.atlassian.net/wiki';
const JIRA_BASE_URL = 'https://freemarkethealth.atlassian.net/jira';
const TABLEAU_BASE_URL = 'https://prod-useast-b.online.tableau.com/#/site/freemarkethealth';

export const Header = () => {
    const { isAuthenticated, logout } = useAuth0();
    const dispatch = useDispatch();
    const {
        showAddAdHocValueCaseAgora = false,
        showOrtfToolInAgora = false,
        showTreatmentMergeQueue = false,
        showWorkflowManagementLayer = false,
    } = useFlags();
    const canUpdateReferralPeriod = usePermissionCheck(WRITE_REFERRAL_PERIOD);

    const [showProfileDialog, setShowProfileDialog] = useState(false);
    const [notificationsDropdownTab, setNotificationsDropdownTab] = useState(
        NotificationsDropdownTabs.Tabs.NOTIFICATIONS,
    );
    // The header also renders on the logged-out page, where there is no token for the request
    const { unreadConversationCount } = useUnreadCounts({ skip: !isAuthenticated });
    const { notifications, incomingNotification } = useSelector((state) => state.notification);

    // Prevent incoming notifications from re-appearing
    useEffect(() => {
        dispatch(clearIncomingNotification());
    }, [incomingNotification, dispatch]);

    const toolsMenuItems = useMemo(
        () => [
            ...(showAddAdHocValueCaseAgora
                ? [
                      {
                          label: 'Bulk Attach Ad Hoc Value Case',
                          to: AppRoutes.bulkAdHocValueCase,
                      },
                  ]
                : []),
            { label: 'Bulk Transfer', to: AppRoutes.bulkTransfer },
            { label: 'Bulk Transition', to: AppRoutes.bulkTransition },
            { label: 'Bulk Unmanage', to: AppRoutes.bulkUnmanage },
            ...(canUpdateReferralPeriod
                ? [
                      {
                          label: 'Bulk Update Referral Expiration',
                          to: AppRoutes.bulkUpdateReferralExpiration,
                      },
                  ]
                : []),
            { label: 'Submit Prior Auth', to: AppRoutes.priorAuthorization },
            ...(showOrtfToolInAgora
                ? [{ label: 'ORTF Processing', to: AppRoutes.ortfProcessing }]
                : []),
            { label: 'Test Referral', to: AppRoutes.testReferral },
            { label: 'Close Claims', to: AppRoutes.closeClaims },
            ...(showTreatmentMergeQueue
                ? [{ label: 'Treatment Merge Queue', to: AppRoutes.treatmentMergeCases }]
                : []),
        ],
        [
            showAddAdHocValueCaseAgora,
            showOrtfToolInAgora,
            showTreatmentMergeQueue,
            canUpdateReferralPeriod,
        ],
    );

    const navTabs = isAuthenticated
        ? [
              { label: 'Referrals', to: AppRoutes.referralsList },
              { label: 'Opportunities', to: AppRoutes.opportunitiesList },
              {
                  label: 'All Messages',
                  to: AppRoutes.allMessages,
                  badgeIntent: Badge.Intent.UNREAD,
                  badgeCount: unreadConversationCount,
              },
              ...(showWorkflowManagementLayer
                  ? [{ label: 'Requests', to: AppRoutes.requests }]
                  : []),
              { label: 'Treatment Search', to: AppRoutes.treatmentSearch },
              {
                  label: 'Tools',
                  to: AppRoutes.tools,
                  children: toolsMenuItems,
              },

              {
                  label: 'Go-Live Planning',
                  to: AppRoutes.goLivePlanning,
                  children: [
                      { label: 'Upcoming Treatments', to: AppRoutes.upcomingTreatments },
                      { label: 'Batches and Scheduling', to: AppRoutes.batchesAndScheduling },
                  ],
              },
          ]
        : [];

    // Load home screen messages & carousels on login
    useGetHomeScreenMessagesQuery(undefined, { skip: !isAuthenticated });

    const handleNotificationsDropdownClose = () => {
        setNotificationsDropdownTab(NotificationsDropdownTabs.Tabs.NOTIFICATIONS);
        const newlySeenIds = notifications
            .filter((notification) => notification.status === BaseNotification.Statuses.NEW)
            .map((notification) => notification.id);
        if (newlySeenIds.length > 0) {
            dispatch(updateNotificationStatuses(BaseNotification.Statuses.SEEN, newlySeenIds));
        }
    };
    const handleNotificationClick = (id) => {
        dispatch(updateNotificationStatus(BaseNotification.Statuses.INTERACTED, id));
    };
    const handleRemoveNotificationClick = (id) => {
        dispatch(updateNotificationStatus(BaseNotification.Statuses.DELETED, id));
    };
    const handleClearAllNotificationsClick = () => {
        const idList = notifications.map((notification) => notification.id);
        dispatch(updateNotificationStatuses(BaseNotification.Statuses.DELETED, idList));
    };

    const onLogOutClick = () => {
        clearDataForLogout();
        logout({ logoutParams: { returnTo: `${window.location.origin}${AppRoutes.loggedOut}` } });
    };

    return (
        <>
            <ProjectBadge />
            <StrataHeader
                navBar={navTabs}
                onLogOutClick={onLogOutClick}
                showProfileDropdown={isAuthenticated}
                onTermsOfUseClick={() => openInNewTab(AppRoutes.termsOfUse)}
                onPrivacyPolicyClick={() => openInNewTab(AppRoutes.privacyPolicy)}
                onProfileClick={() => setShowProfileDialog(true)}
                showNotifications={isAuthenticated}
                notifications={notifications}
                incomingNotification={incomingNotification}
                NotificationComponentMap={NotificationComponentMap}
                onNotificationClick={handleNotificationClick}
                onRemoveNotificationClick={handleRemoveNotificationClick}
                onClearAllNotificationsClick={handleClearAllNotificationsClick}
                onNotificationsDropdownClose={handleNotificationsDropdownClose}
                hideNotificationList={
                    notificationsDropdownTab === NotificationsDropdownTabs.Tabs.WATCHING
                }
                notificationsDropdownHeader={
                    <NotificationsDropdownTabs
                        notifications={notifications}
                        activeTab={notificationsDropdownTab}
                        onActiveTabChange={setNotificationsDropdownTab}
                    />
                }
                appVersion={process.env.REACT_APP_VERSION}
                fmhEnvironmentForTag={process.env.REACT_APP_ENV}
                launchpad={
                    isAuthenticated && {
                        currentApp: StrataHeader.FMHApplication.AGORA,
                        fmhEnvironment: process.env.REACT_APP_ENV,
                        /* eslint-disable no-secrets/no-secrets -- external dashboard URLs, not secrets */
                        externalApplications: [
                            {
                                title: 'Workflow',
                                links: [
                                    {
                                        label: 'Confluence',
                                        href: `${CONFLUENCE_BASE_URL}/home`,
                                    },
                                    {
                                        label: 'Databricks',
                                        href: 'https://adb-3629126448578115.15.azuredatabricks.net/login.html?o=3629126448578115&next_url=%2Fsql%3Fo%3D3629126448578115&tuuid=d62df2ea-9acb-4c2b-a719-0f74f8b8e236',
                                    },
                                    {
                                        label: 'Dialpad',
                                        href: 'https://dialpad.com/login',
                                    },
                                    {
                                        label: 'ICE Tickets',
                                        href: `${JIRA_BASE_URL}/servicedesk/projects/ICE/queues/custom/191`,
                                    },
                                    {
                                        label: 'Jira',
                                        href: `${JIRA_BASE_URL}/for-you`,
                                    },
                                    {
                                        label: 'Monday',
                                        href: 'https://free-market-health.monday.com/',
                                    },
                                ],
                            },
                            {
                                title: 'Clinical',
                                links: [
                                    {
                                        label: 'CSP',
                                        href: 'https://www.controlcenter.com/',
                                    },
                                    {
                                        label: 'CoverMyMeds',
                                        href: 'https://www.covermymeds.health/',
                                    },
                                    {
                                        label: 'HMK - Biosimilar Routing',
                                        href: `${CONFLUENCE_BASE_URL}/spaces/OP/pages/3527573505/Handling+Biosimilars#Highmark-Biosimilar-Routing`,
                                    },
                                    {
                                        label: 'HMK - Formulary',
                                        href: 'https://client.formularynavigator.com/Search.aspx?siteCode=9866474260',
                                    },
                                    {
                                        label: 'HWC - Caremark',
                                        href: 'https://client.caremark.com/ClientPortal#Z7_AGVUQ0C76O7T30IIM0SREB1I66',
                                    },
                                    {
                                        label: 'HWC - MedHok',
                                        href: 'https://appstore.gatewayhealthplan.com/logon/LogonPoint/tmindex.html',
                                    },
                                    {
                                        label: 'PA Logic',
                                        href: 'https://rxsense.palogic.com/',
                                    },
                                    {
                                        label: 'Prime Applications',
                                        href: 'https://ciam.primetherapeutics.com/oauth2/v1/authorize?client_id=okta.2b1959c8-bcc0-56eb-a589-cfcfb7422f26&code_challenge=q8P1B0GTwb5tJoFiE31fP4fZImxCLqGbbE3OOqC6lhs&code_challenge_method=S256&nonce=qLhzv0Cve2okAklw23DY0LuyG1eApnKzxd1M7tTxh9fWgEw6MZMUC6Gyt7YXlgSe&redirect_uri=https%3A%2F%2Fciam.primetherapeutics.com%2Fenduser%2Fcallback&response_type=code&state=pFYNLwfNAbUe31QOOwOJkJthcb0AOZKwN1sEHXq7gF1CCYi5n4CUlIvpYLn2inSY&scope=openid%20profile%20email%20okta.users.read.self%20okta.users.manage.self%20okta.internal.enduser.read%20okta.internal.enduser.manage%20okta.enduser.dashboard.read%20okta.enduser.dashboard.manage%20okta.myAccount.sessions.manage',
                                    },
                                    {
                                        label: 'RxSense - RxIQ',
                                        href: 'https://webadmin.rxsense.com/login',
                                    },
                                ],
                            },
                            {
                                title: 'Reports',
                                links: [
                                    {
                                        label: 'Claim Rejections',
                                        href: `${TABLEAU_BASE_URL}/views/RejectedClaimsMonitoring/RejectedClaimsMonitoring?:iid=1`,
                                    },
                                    {
                                        label: 'HMK Biosimilar Opportunity Tracker',
                                        href: `${TABLEAU_BASE_URL}/views/HighmarkSimlandiOpportunityTracker/HighmarkBiosimilarOpportunityTracker?:iid=1`,
                                    },
                                    {
                                        label: 'LITE Pharmacy Daily Worklist',
                                        href: `${TABLEAU_BASE_URL}/views/LitePharmacyDailyWorklist/DailyWorklist-AllNon-OnboardedRequests?:iid=1`,
                                    },
                                    {
                                        label: 'Member Onboarding',
                                        href: `${TABLEAU_BASE_URL}/views/MemberOnboardingReport/MemberOnboarding`,
                                    },
                                    {
                                        label: 'Open Claims Aging',
                                        href: `${TABLEAU_BASE_URL}/views/Operations-OpenClaimsAgingTool/OpenClaimsAging`,
                                    },
                                    {
                                        label: "Patient's with Duplicative Treatments",
                                        href: `${TABLEAU_BASE_URL}/views/PatientswithDuplicativeTreatments/DuplicativeReferralDetail?:iid=1`,
                                    },
                                    {
                                        label: 'PG Scorecard',
                                        href: `${TABLEAU_BASE_URL}/workbooks/3341211/views`,
                                    },
                                    {
                                        label: 'Pharmacy Claims Search',
                                        href: `${TABLEAU_BASE_URL}/views/PharmacyClaimsSearch/PharmacyClaimsSearch`,
                                    },
                                    {
                                        label: 'Poly Pharmacy',
                                        href: `${TABLEAU_BASE_URL}/views/PolypharmacyMonitoring/PolypharmacyMonitoring?:iid=1`,
                                    },
                                    {
                                        label: 'Pre-Bid Templates',
                                        href: `${TABLEAU_BASE_URL}/views/AuctionPre-BidTemplates/AuctionPre-BidTemplates`,
                                    },
                                    {
                                        label: 'Referral Queue',
                                        href: `${TABLEAU_BASE_URL}/workbooks/2588927/views`,
                                    },
                                    {
                                        label: 'Rejected Claims Monitoring',
                                        href: `${TABLEAU_BASE_URL}/views/RejectedClaimsMonitoring/RejectedClaimsMonitoring?:iid=1`,
                                    },
                                ],
                            },
                        ],
                        /* eslint-enable no-secrets/no-secrets */
                    }
                }
            />
            {isAuthenticated && (
                <>
                    <FeatureCarousel />
                    <UserProfileDialog
                        isOpen={showProfileDialog}
                        onCancel={() => setShowProfileDialog(false)}
                    />
                </>
            )}
        </>
    );
};
